import RoomleConfiguratorApi from '@roomle/embedding-lib';
import { addToCart } from '../lib/cart.js';

export default (node) => {
  // Validate node exists
  if (!node || !node.dataset) {
    console.error('Invalid node or missing dataset');
    return;
  }

  // Extract configuration from node dataset with defaults
  const overrideConfigurationId = node.dataset.overrideConfigurationId || '';
  const fallbackConfigurationId = node.dataset.fallbackConfigurationId || '';
  const roomleLocale = node.dataset.roomleLocale || 'en';
  const roomleOverrideCountry = node.dataset.roomleOverrideCountry || '';
  const roomleCurrency = node.dataset.roomleCurrency || 'EUR';
  let roomleDeeplink = node.dataset.roomleDeeplink || '';
  // Sanitize deeplink: Roomle breaks if attribution params (&utm= etc.) are present
  // Remove everything from the first '&' onward.
  if (roomleDeeplink.includes('&')) {
    roomleDeeplink = roomleDeeplink.split('&')[0];
  }
  const appCss = node.dataset.appCss || '';
  const store = node.dataset.store || 'COM';

  // Validate required values
  if (!fallbackConfigurationId && !overrideConfigurationId) {
    console.error('No configuration ID provided in dataset');
    return;
  }

  // Component state
  let activeVariants = [];

  /**
   * Utility functions
   */
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  const extractNumericId = (variantId) => {
    if (!variantId || typeof variantId !== 'string') {
      console.warn('Invalid variant ID:', variantId);
      return null;
    }
    return variantId.split('/').pop();
  };

  /**
   * Configuration functions
   */
  const determineRoomleId = () => {
    // 1. Explicit override takes precedence
    if (overrideConfigurationId && overrideConfigurationId.length) {
      console.log(
        'Using override_configuration_id from settings:',
        overrideConfigurationId
      );
      return overrideConfigurationId;
    }

    // 2. Attempt to read from current URL's id= query param (strip tracking params like &_gl, utm, etc.)
    let extractedId = null;
    try {
      const current = new URL(window.location.href);
      const rawId = current.searchParams.get('id');
      if (rawId) {
        // Although URLSearchParams already stops at '&', be defensive in case of malformed strings
        extractedId = rawId.split('&')[0].split('#')[0];
      }
    } catch (e) {
      // Fallback: manual regex parse if URL constructor fails (very unlikely)
      const href = window.location.href;
      const match = href.match(/[?&]id=([^&#]+)/);
      if (match && match[1]) {
        extractedId = match[1].split('&')[0].split('#')[0];
      }
    }

    if (extractedId) {
      console.log('Extracted sanitized roomleId from URL:', extractedId);
      return extractedId;
    }

    // 3. Fallback configuration ID
    console.log(
      'Using fallback_configuration_id from settings:',
      fallbackConfigurationId
    );
    return fallbackConfigurationId;
  };

  const createConfiguratorOptions = (roomleId) => {
    return {
      id: roomleId,
      locale: roomleLocale,
      overrideCountry: roomleOverrideCountry,
      autoStart: true,
      skin: {
        'primary-color': '#383838',
        'color-on-primary': '#fff',
        'cta-color': '#383838',
        'color-on-cta': '#fff',
      },
      fonts: {
        source: appCss,
        family: 'teka',
      },
      // Provide sanitized deeplink without tracking / attribution parameters
      deeplink: roomleDeeplink,
    };
  };

  /**
   * Variant management functions
   */
  const fetchVariantsBySku = async (sku) => {
    console.log('Fetching variants by SKU:', sku, 'for store:', store);
    try {
      const response = await fetch(
        `https://fermliving-express-variant-fetcher.vercel.app/variant-by-sku/${sku}?store=${store}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch variants for SKU: ${sku}`);
      }

      const data = await response.json();
      console.log('Data', data);
      return data.variant;
    } catch (error) {
      console.error('Error fetching variants:', error);
      return undefined;
    }
  };

  const updateActiveVariants = async (partList) => {
    if (!partList || !partList.fullList || !Array.isArray(partList.fullList)) {
      console.warn('Invalid partList provided to updateActiveVariants');
      return;
    }

    const parts = partList.fullList.flatMap((part) => part).filter(Boolean);
    const skus = parts
      .filter((p) => p && p.articleNr)
      .map((p) => String(p.articleNr));

    // Remove variants not in current part list
    activeVariants = activeVariants.filter((variant) =>
      skus.includes(variant.sku)
    );

    // Fetch missing variants
    const skusToFetch = skus.filter(
      (sku) => !activeVariants.some((variant) => variant.sku === sku)
    );

    const variants = await Promise.all(
      skusToFetch.map((sku) => fetchVariantsBySku(sku))
    );

    // Add only new variants (additional safety check)
    const newVariants = variants.filter(
      (variant) => variant && !activeVariants.some((v) => v.sku === variant.sku)
    );

    activeVariants.push(...newVariants);

    // Update quantities
    updateVariantQuantities(parts);
  };

  const updateVariantQuantities = (parts) => {
    if (!Array.isArray(parts)) {
      console.warn('Invalid parts array provided to updateVariantQuantities');
      return;
    }

    activeVariants.forEach((variant) => {
      if (!variant || !variant.sku) {
        console.warn('Invalid variant in activeVariants:', variant);
        return;
      }

      const part = parts.find((p) => p && p.articleNr === variant.sku);
      if (part && typeof part.count === 'number') {
        variant.quantity = part.count;
      } else {
        variant.quantity = variant.quantity || 1; // Default to 1 if no count found
      }
    });
  };

  const getAllVariantsForParts = async (partList) => {
    if (!partList || !partList.fullList || !Array.isArray(partList.fullList)) {
      console.warn('Invalid partList provided to getAllVariantsForParts');
      return [];
    }

    const partsList = partList.fullList.flatMap((part) => part).filter(Boolean);
    const skus = partsList
      .filter((p) => p && p.articleNr)
      .map((p) => String(p.articleNr));

    // Fetch any missing variants
    const missingSkus = skus.filter(
      (sku) => !activeVariants.some((variant) => variant.sku === sku)
    );

    const missingVariants = await Promise.all(
      missingSkus.map((sku) => fetchVariantsBySku(sku))
    );

    // Combine all variants
    const allVariants = [...activeVariants, ...missingVariants.filter(Boolean)];

    // Update quantities
    allVariants.forEach((variant) => {
      if (!variant || !variant.sku) {
        console.warn('Invalid variant in allVariants:', variant);
        return;
      }

      const part = partsList.find((p) => p && p.articleNr === variant.sku);
      if (part && typeof part.count === 'number') {
        variant.quantity = part.count;
      } else {
        variant.quantity = variant.quantity || 1; // Default to 1 if no count found
      }
    });

    return allVariants;
  };

  const calculateTotalPrice = () => {
    if (!Array.isArray(activeVariants) || activeVariants.length === 0) {
      return { price: 0, compareAtPrice: 0 };
    }

    const totals = activeVariants.reduce(
      (acc, variant) => {
        if (!variant) {
          console.warn('Invalid variant in calculateTotalPrice:', variant);
          return acc;
        }

        const price = (Number(variant.price) || 0) * (variant.quantity || 1);
        const compareAtPrice =
          variant.compareAtPrice && Number(variant.compareAtPrice) > 0
            ? Number(variant.compareAtPrice) * (variant.quantity || 1)
            : price;

        // Validate numeric results
        if (isNaN(price) || isNaN(compareAtPrice)) {
          console.warn('Invalid price calculation for variant:', variant);
          return acc;
        }

        return {
          price: acc.price + price,
          compareAtPrice: acc.compareAtPrice + compareAtPrice,
        };
      },
      { price: 0, compareAtPrice: 0 }
    );

    return totals;
  };

  /**
   * Locale mapping for price formatting
   */
  const getLocaleForStore = (storeCode) => {
    const localeMap = {
      US: 'en-US',
      UK: 'en-GB',
      SE: 'sv-SE',
      DK: 'da-DK',
      NL: 'nl-NL',
      DE: 'de-DE',
      FR: 'fr-FR',
      COM: 'en-GB',
      DEV_COM: 'en-GB',
    };

    return localeMap[storeCode] || 'en-GB';
  };

  /**
   * Price management
   */
  const updatePriceDisplay = (totals) => {
    if (!totals || typeof totals.price !== 'number') {
      console.warn('Invalid totals provided to updatePriceDisplay:', totals);
      return;
    }

    const priceDisplayElement = document.querySelector('[data-price-display]');
    const originalPriceElement = document.querySelector(
      '[data-price-display-original]'
    );
    const locale = getLocaleForStore(store);

    if (priceDisplayElement) {
      try {
        priceDisplayElement.innerText = `${roomleCurrency} ${totals.price.toLocaleString(
          locale,
          {
            style: 'decimal',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }
        )}`;
      } catch (error) {
        console.error('Error formatting price:', error);
        priceDisplayElement.innerText = `${roomleCurrency} ${totals.price.toFixed(2)}`;
      }
    }

    if (
      originalPriceElement &&
      typeof totals.compareAtPrice === 'number' &&
      totals.compareAtPrice > 0 &&
      totals.compareAtPrice > totals.price
    ) {
      try {
        originalPriceElement.innerText = `${roomleCurrency} ${totals.compareAtPrice.toLocaleString(
          locale,
          {
            style: 'decimal',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }
        )}`;
        originalPriceElement.style.display = 'inline';
      } catch (error) {
        console.error('Error formatting compare at price:', error);
        originalPriceElement.innerText = `${roomleCurrency} ${totals.compareAtPrice.toFixed(2)}`;
        originalPriceElement.style.display = 'inline';
      }
    } else if (originalPriceElement) {
      originalPriceElement.style.display = 'none';
    }
  };

  /**
   * Event handlers
   */
  const handlePartListUpdate = debounce(async (partList) => {
    console.log('Partlist in update', partList);
    await updateActiveVariants(partList);
    const totals = calculateTotalPrice();
    updatePriceDisplay(totals);
  }, 300);

  const handleRequestProduct = async (configurationId, image, partlist) => {
    console.log('Partlist in request', partlist);
    try {
      const allVariants = await getAllVariantsForParts(partlist);
      console.log('All variants', allVariants);

      if (!Array.isArray(allVariants) || allVariants.length === 0) {
        console.warn('No variants found for partlist');
        return;
      }

      // Prepare cart items
      const items = allVariants
        .filter((variant) => variant && variant.id && variant.quantity > 0)
        .map((variant) => {
          const numericId = extractNumericId(variant.id);
          if (!numericId) {
            console.warn('Could not extract numeric ID from variant:', variant);
            return null;
          }
          return {
            id: numericId,
            quantity: variant.quantity || 1,
          };
        })
        .filter(Boolean);

      if (items.length === 0) {
        console.warn('No valid items to add to cart');
        return;
      }

      // Add to cart and open drawer
      await addToCart(items);
      document.dispatchEvent(new CustomEvent('cart:open'));
    } catch (error) {
      console.error('Error adding items to cart:', error);
    }
  };

  const handleButtonClicked = (name) => {
    console.log('Built-in button clicked, event name:', name);
  };

  const setupCustomEventListeners = (configurator) => {
    const saveButton = document.getElementById('trigger-save');
    const requestButton = document.getElementById('trigger-request');

    if (saveButton) {
      saveButton.addEventListener('click', async () => {
        try {
          await configurator.ui.triggerShowOverlay(1);
        } catch (error) {
          console.error('Error triggering save overlay:', error);
        }
      });
    }

    if (requestButton) {
      requestButton.addEventListener('click', async () => {
        try {
          await configurator.ui.triggerRequestProduct();
        } catch (error) {
          console.error('Error triggering request product:', error);
        }
      });
    }
  };

  /**
   * Initialization
   */
  const init = async () => {
    try {
      // Check if container exists
      const container = document.getElementById('configurator-container');
      if (!container) {
        throw new Error('Configurator container element not found');
      }

      // Determine configuration ID
      const roomleId = determineRoomleId();
      if (!roomleId) {
        throw new Error('No valid Roomle configuration ID found');
      }

      // Create configurator options
      const options = createConfiguratorOptions(roomleId);

      // Initialize configurator
      const configurator = await RoomleConfiguratorApi.createConfigurator(
        'fermliving',
        container,
        options
      );

      // Validate configurator and its properties
      if (!configurator || !configurator.ui || !configurator.ui.callbacks) {
        throw new Error('Invalid configurator instance returned');
      }

      // Set up callbacks
      configurator.ui.callbacks.onPartListUpdate = handlePartListUpdate;
      configurator.ui.callbacks.onRequestProduct = handleRequestProduct;
      configurator.ui.callbacks.onButtonClicked = handleButtonClicked;

      // Set up custom event listeners
      setupCustomEventListeners(configurator);
    } catch (error) {
      console.error('Error initializing Roomle configurator:', error);

      // Show user-friendly error message
      const container = document.getElementById('configurator-container');
      if (container) {
        container.innerHTML =
          '<div style="padding: 20px; text-align: center; color: #666;">Unable to load configurator. Please try refreshing the page.</div>';
      }
    }
  };

  // Initialize the component
  init();
};
